import React, { useMemo, useRef, useState } from 'react';
import Select, { components } from 'react-select';
import { useIntl } from 'react-intl';
import { Service, ServiceModal } from 'modules/services';
import { Unit, UnitModal } from 'modules/units';
import { SelectList, getSelectStyles } from 'shared/components';
import { useMobile, useModal } from 'shared/hooks';
import { useProposalAICreatedCatalog } from '../context';
import { CatalogPreviewOption, normalizeCatalogPreviewName } from '../utils';
import { ProposalAICreatedCatalogBadge } from './ProposalAICreatedCatalogBadge';

interface Props<T extends Service | Unit> {
  blockIndex: number;
  buttonType: 'service' | 'unit';
  labelId: string;
  placeholderId: string;
  options: CatalogPreviewOption<T>[];
  value: CatalogPreviewOption<T>;
  onChange: (option: CatalogPreviewOption<T> | null) => void;
  renderModal: (params: {
    inputValue: string;
    onClose: VoidFunction;
    onCreate: (entity: T) => void;
  }) => React.ReactNode;
}

function toExistingCatalogPreviewOption<T extends Service | Unit>(
  entity: T,
): CatalogPreviewOption<T> {
  return {
    kind: 'existing',
    label: entity.name,
    name: entity.name,
    value: `existing:${normalizeCatalogPreviewName(entity.name)}`,
    entity,
  };
}

export function ProposalAIGeneratorCatalogSelect<T extends Service | Unit>({
  blockIndex,
  buttonType,
  labelId,
  placeholderId,
  options,
  value,
  onChange,
  renderModal,
}: Props<T>) {
  const isMobile = useMobile();
  const { formatMessage } = useIntl();
  const { openModal, closeModal, isModalOpen } = useModal();
  const {
    isNewlyCreatedService,
    isNewlyCreatedUnit,
    markNewlyCreatedService,
    markNewlyCreatedUnit,
  } = useProposalAICreatedCatalog();
  const selectWrapperRef = useRef<HTMLDivElement>(null);
  const [modalInputValue, setModalInputValue] = useState('');

  const initialModalValue = useMemo(() => {
    if (modalInputValue.trim()) {
      return modalInputValue.trim();
    }

    return value.kind === 'create' ? value.name : '';
  }, [modalInputValue, value.kind, value.name]);

  function handleButtonClick() {
    selectWrapperRef.current?.querySelector('input')?.blur();
    openModal();
  }

  function handleCreate(entity: T) {
    if (buttonType === 'service') {
      markNewlyCreatedService(entity as Service);
    } else {
      markNewlyCreatedUnit(entity as Unit);
    }

    onChange(toExistingCatalogPreviewOption(entity));
    setModalInputValue(entity.name);
    closeModal();
  }

  const shouldRenderCreatedBadge =
    buttonType === 'service'
      ? isNewlyCreatedService(value.entity?.id)
      : isNewlyCreatedUnit(value.entity?.id);

  return (
    <div className="field m-bottom-0">
      <label className="t-label t-text-2 m-bottom--tny">
        {formatMessage({ id: labelId })}
        {shouldRenderCreatedBadge && <ProposalAICreatedCatalogBadge />}
      </label>
      <div ref={selectWrapperRef}>
        <Select
          options={options}
          value={value}
          onChange={(selectedOption) =>
            onChange(selectedOption as CatalogPreviewOption<T> | null)
          }
          onInputChange={(inputValue) => {
            setModalInputValue(inputValue);

            return inputValue;
          }}
          styles={getSelectStyles(false, 1)}
          className={`proposal-ai-generator__preview-select proposal-ai-generator__preview-select--${buttonType}-${blockIndex}`}
          classNamePrefix="proposal-ai-generator__preview-select"
          placeholder={formatMessage({ id: placeholderId })}
          isSearchable={!isMobile}
          isDisabled={isModalOpen}
          components={{
            MenuList: ({ children, ...rest }) => (
              <components.MenuList {...rest} className="select__adder">
                <SelectList
                  onButtonClick={handleButtonClick}
                  buttonType={buttonType}
                  btnSelector={`proposal-ai-add-${buttonType}-${blockIndex}`}
                >
                  {children}
                </SelectList>
              </components.MenuList>
            ),
          }}
        />
      </div>

      {isModalOpen &&
        renderModal({
          inputValue: initialModalValue,
          onClose: closeModal,
          onCreate: handleCreate,
        })}
    </div>
  );
}

export const ProposalAIGeneratorServiceCatalogSelect: React.FC<
  Omit<Props<Service>, 'buttonType' | 'labelId' | 'placeholderId' | 'renderModal'>
> = (props) => (
  <ProposalAIGeneratorCatalogSelect<Service>
    {...props}
    buttonType="service"
    labelId="dropdowns.service.label"
    placeholderId="dropdowns.service.placeholder"
    renderModal={({ inputValue, onClose, onCreate }) => (
      <ServiceModal
        inputValue={inputValue}
        onClose={onClose}
        onCreate={onCreate}
      />
    )}
  />
);

export const ProposalAIGeneratorUnitCatalogSelect: React.FC<
  Omit<Props<Unit>, 'buttonType' | 'labelId' | 'placeholderId' | 'renderModal'>
> = (props) => (
  <ProposalAIGeneratorCatalogSelect<Unit>
    {...props}
    buttonType="unit"
    labelId="dropdowns.unit.label"
    placeholderId="dropdowns.unit.placeholder"
    renderModal={({ inputValue, onClose, onCreate }) => (
      <UnitModal
        inputValue={inputValue}
        onClose={onClose}
        onCreate={onCreate}
      />
    )}
  />
);
